import global from "./global";
import i18next from "i18next";
import Backend from "i18next-http-backend";

const log = global.log.getLogger("f_mtac.global.internationalization");
log.setLevel(log.levels.INFO);

// admin boolean added in order to manage differences between Tacteam app and (super)admin page
const Internationalization = function (admin) {
    // Default language if the language of the browser is not present
    const defaultLanguage = "en-US";

    // Init the i18next module
    // - Set language
    // - Load translations from backend
    this.init = async function () {
        log.debug("Internationalization:init");

        // Restore language saved in configuration or use navigator's language if supported
        var lang = global.configuration.internationalization?.language;
        if (!lang) {
            log.debug("Searching for supported language corresponding to the navigator's language...");
            lang = await global.server
                .mtac_language_list()
                .then((languages) => languages.data.list.find((language) => language.name.startsWith(navigator.language)))
                .then((language) => language?.name);
        }
        log.debug({lang});

        await i18next.use(Backend).init(
            {
                debug: false,
                lng: lang,
                backend: {
                    loadPath: "/mtac/static/locales/{{lng}}/translation.json"
                },
                fallbackLng: defaultLanguage,
                load: "currentOnly"
            },
            (error) => (error ? log.warn(error) : null)
        );
        log.info("Language:", i18next.language);
        // selected = false because not selected by the user yet (done in UserArea.js)
        global.configuration.add_store("internationalization", {language: i18next.language, selected: false});
        this.updateDocument();
    };

    // Do translations if find data tags:
    // - data-i18n-innerhtml
    // - data-i18n-placeholder
    // - data-i18n-arialabel
    this.updateDocument = () => {
        log.debug("Updating document...");
        document.querySelectorAll("[data-i18n-innerhtml]").forEach((el) => (el.innerHTML = i18next.t(el.dataset.i18nInnerhtml)));
        document.querySelectorAll("[data-i18n-placeholder]").forEach((el) => (el.placeholder = i18next.t(el.dataset.i18nPlaceholder)));
        document.querySelectorAll("[data-i18n-arialabel]").forEach((el) => (el.ariaLabel = i18next.t(el.dataset.i18nArialabel)));
        document.querySelectorAll("[data-i18n-insert-afterbegin]").forEach((el) => {
            // check if it has already been inserted
            if (el.textContent == "") {
                log.debug("Found", el);
                el.insertAdjacentText("afterbegin", i18next.t(el.dataset.i18nInsertAfterbegin));
            }
        });
    };

    // Get the text of a given key
    this.text = (key, options) => i18next.t(key, options);

    // format the time without the seconds
    this.formatTime = (date) => {
        let options = {hour: "2-digit", minute: "2-digit"};
        return date.toLocaleTimeString(i18next.language, options);
    };

    // format the time with the seconds
    this.formatTimeWithSeconds = (date) => {
        let options = {hour: "2-digit", minute: "2-digit", second: "2-digit"};
        return date.toLocaleTimeString(i18next.language, options);
    };

    // format the date (without the time and the year)
    this.formatDate = (date) => {
        let options = {day: "numeric", month: "numeric"};
        return date.toLocaleDateString(i18next.language, options);
    };

    // format the date (with the year)
    this.formatDateWithYear = (date) => {
        let options = {day: "numeric", month: "numeric", year: "numeric"};
        return date.toLocaleDateString(i18next.language, options);
    };

    // format the date (without the time and the year)
    this.formatDateFromAgnet = (date) => {
        let formattedDate;
        let year = date.slice(0, 4);
        let month = date.slice(4, 6);
        let day = date.slice(6, 8);
        let hms = date.slice(9);

        if (i18next.language === "en-US") {
            formattedDate = month + "/" + day + "/" + year + " " + hms;
        } else {
            formattedDate = day + "/" + month + "/" + year + " " + hms;
        }
        return formattedDate;
    };

    this.changeLanguage = async function (lang) {
        log.info("changeLanguage: " + lang);
        try {
            await i18next.changeLanguage(lang);
        } catch (err) {
            log.error("error:" + err);
            return;
        }

        // change the data tags
        this.updateDocument();

        // change 'lang' and 'xml:lang' tags
        // get 'xx" from 'xx-yy'
        const code = lang.substring(0, 2);
        document.documentElement.lang = code;
        $("html").attr("xml:lang", code);

        // store the new language
        global.configuration.internationalization.selected = true;
        global.configuration.internationalization.language = lang;
        await global.configuration.commit_store("internationalization");
        if (!admin) {
            global.event_bus.publish("internationalization.language.changed");
        }
    };
};

export default Internationalization;

global.app.bootstrap.register_module({
    name: "internationalization",
    deps: ["app", "dom_loaded", "configuration"],
    init: async () => {
        log.debug("Internationalization:register_module");
        global.internationalization = new Internationalization(false);
        await global.internationalization.init();
    }
});
